import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Button, Space, Tag, Typography, Form, Input, Select, DatePicker, Radio, Modal, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import SearchSelect from '../../../components/SearchSelect/SearchSelect';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const AUDIT_STATUS = {
  pending: { text: '待审核', color: 'processing' },
  approved: { text: '已通过', color: 'success' },
  rejected: { text: '已驳回', color: 'error' },
};

// TODO: 联调时替换为接口数据
const MOCK_AUDIT = [
  { key: '1', productName: 'NVR网络录像机方案', productId: 'P_ECO_004', category: '存储类', vendorName: '东莞康林物联网有限公司', vendorId: 'ENT_V03', openRange: 'public', status: 'pending', submitTime: '2026-04-22 11:00:00', auditTime: null, hardwarePrice: 199.00 },
  { key: '2', productName: '智能云台摄像机方案', productId: 'P_ECO_001', category: '摄像机', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', openRange: 'public', status: 'approved', submitTime: '2026-04-19 10:00:00', auditTime: '2026-04-20 09:30:00', hardwarePrice: 89.00 },
  { key: '3', productName: '4G低功耗电池摄像机方案', productId: 'P_ECO_002', category: '摄像机', vendorName: '杭州云联物联网有限公司', vendorId: 'ENT_V02', openRange: 'public', status: 'approved', submitTime: '2026-04-17 14:00:00', auditTime: '2026-04-18 10:00:00', hardwarePrice: 129.00 },
  { key: '4', productName: '智能门铃方案', productId: 'P_ECO_003', category: '摄像机', vendorName: '深圳芯视界科技有限公司', vendorId: 'ENT_V01', openRange: 'directed', directedEnts: [{ name: '东莞康林物联网有限公司', id: 'JGSf0Nd7' }, { name: '成都云视科技有限公司', id: 'CD-YS-002' }], status: 'rejected', submitTime: '2026-04-14 09:00:00', auditTime: '2026-04-15 08:30:00', hardwarePrice: 65.00 },
];

// TODO: 联调时替换为真实接口
const fetchProductOptions = (keyword) =>
  new Promise((resolve) => setTimeout(() => resolve(
    MOCK_AUDIT.filter((p) => p.productName.includes(keyword)).map((p) => ({ value: p.productId, label: p.productName }))
  ), 300));

const fetchVendorOptions = (keyword) =>
  new Promise((resolve) => setTimeout(() => resolve(
    [...new Map(MOCK_AUDIT.filter((p) => p.vendorName.includes(keyword)).map((p) => [p.vendorId, { value: p.vendorId, label: p.vendorName }])).values()]
  ), 300));

const EcosystemAudit = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [auditForm] = Form.useForm();
  const [auditVisible, setAuditVisible] = useState(false);
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditResult, setAuditResult] = useState('pass');

  const handleAuditSubmit = () => {
    auditForm.validateFields().then(values => {
      if (values.result === 'reject' && !values.reason?.trim()) { message.warning('请输入驳回原因'); return; }
      message.success(values.result === 'pass' ? '审核已通过' : '已驳回');
      setAuditVisible(false);
    });
  };

  const columns = [
    {
      title: '方案产品', key: 'product', width: 220,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.productName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.productId}</Text></div>),
    },
    { title: '产品类目', dataIndex: 'category', width: 200 },
    {
      title: '方案商企业', key: 'vendor', width: 200,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.vendorName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.vendorId}</Text></div>),
    },
    { title: '开放范围', dataIndex: 'openRange', width: 80, render: (v) => <Tag color={v === 'public' ? 'blue' : 'orange'}>{v === 'public' ? '公开' : '定向'}</Tag> },
    { title: '硬件定价', dataIndex: 'hardwarePrice', width: 100, align: 'right', render: (v) => v != null ? `¥${v.toFixed(2)}` : '-' },
    { title: '审核状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={AUDIT_STATUS[v]?.color}>{AUDIT_STATUS[v]?.text}</Tag> },
    { title: '提交时间', dataIndex: 'submitTime', width: 170 },
    { title: '审核时间', dataIndex: 'auditTime', width: 170, render: (v) => v || <span style={{ color: '#ccc' }}>-</span> },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/product/ecosystem/detail?id=${r.productId}&status=${r.status}`, { state: r })}>查看</Button>
          {r.status === 'pending' && <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => { setAuditRecord(r); setAuditResult('pass'); auditForm.resetFields(); auditForm.setFieldsValue({ result: 'pass' }); setAuditVisible(true); }}>审核</Button>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>生态方案审核</Title></div>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline">
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="prodType" initialValue="name" noStyle><Select style={{ width: 100 }}><Select.Option value="name">产品名称</Select.Option><Select.Option value="id">产品ID</Select.Option></Select></Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.prodType !== cur.prodType}>
                {({ getFieldValue }) => getFieldValue('prodType') === 'id'
                  ? <Form.Item name="prodValue" noStyle><Input placeholder="输入产品ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="prodValue" noStyle><SearchSelect fetchOptions={fetchProductOptions} placeholder="搜索方案产品" style={{ width: 200 }} /></Form.Item>}
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="vendorType" initialValue="name" noStyle><Select style={{ width: 100 }}><Select.Option value="name">方案商</Select.Option><Select.Option value="id">企业ID</Select.Option></Select></Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.vendorType !== cur.vendorType}>
                {({ getFieldValue }) => getFieldValue('vendorType') === 'id'
                  ? <Form.Item name="vendorValue" noStyle><Input placeholder="输入企业ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="vendorValue" noStyle><SearchSelect fetchOptions={fetchVendorOptions} placeholder="搜索方案商" style={{ width: 200 }} /></Form.Item>}
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="status" style={{ marginRight: 8 }}><Select placeholder="审核状态" allowClear style={{ width: 120 }} options={Object.entries(AUDIT_STATUS).map(([k, v]) => ({ value: k, label: v.text }))} /></Form.Item>
          <Form.Item name="submitTime" style={{ marginRight: 8 }}><RangePicker placeholder={['提交开始', '提交结束']} /></Form.Item>
          <Form.Item><Space><Button type="primary" icon={<SearchOutlined />}>查询</Button><Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button></Space></Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Table columns={columns} dataSource={MOCK_AUDIT} rowKey="key" scroll={{ x: 1300 }} style={{ whiteSpace: 'nowrap' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
      </Card>

      <Modal title="审核方案上架申请" open={auditVisible} onCancel={() => setAuditVisible(false)} onOk={handleAuditSubmit} okText="提交审核" width={520} destroyOnClose>
        <Form form={auditForm} layout="vertical" style={{ marginTop: 16 }} initialValues={{ result: 'pass' }}>
          {auditRecord && (
            <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ marginBottom: 4 }}><Text type="secondary">方案产品：</Text><Text strong>{auditRecord.productName}</Text></div>
              <div><Text type="secondary">方案企业：</Text><Text>{auditRecord.vendorName}（{auditRecord.vendorId}）</Text></div>
            </div>
          )}
          <Form.Item name="result" label="审核结果" rules={[{ required: true }]}>
            <Radio.Group onChange={(e) => setAuditResult(e.target.value)}>
              <Radio value="pass">审核通过</Radio>
              <Radio value="reject">审核驳回</Radio>
            </Radio.Group>
          </Form.Item>
          {auditResult === 'reject' && (
            <Form.Item name="reason" label="驳回原因" rules={[{ required: true, message: '请输入驳回原因' }]}>
              <TextArea rows={4} placeholder="请输入驳回原因" maxLength={300} showCount />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default EcosystemAudit;
